"use client"

import { useEffect, useMemo, useState } from "react"

import {
  compareSemver,
  stripRange,
  type DependencySnapshot,
  type UpdateStatus,
} from "@/lib/dependency-snapshot-types"

type Row = DependencySnapshot & {
  latest: string | null
  status: UpdateStatus | "loading"
  error?: string
}

/**
 * Client component for the /admin/stack view.
 *
 * Intentionally uses Payload's own design primitives (semantic admin
 * classes, inline styles tied to Payload's CSS variables) instead of
 * the site-wide shadcn components — the view lives inside Payload's
 * admin chrome, so pulling in the storefront Button/Card would clash
 * visually and drag extra bundle weight into the admin route.
 */
async function fetchLatest(pkg: string): Promise<string> {
  const res = await fetch(`https://registry.npmjs.org/${pkg}`, {
    headers: { Accept: "application/vnd.npm.install-v1+json" },
  })
  if (!res.ok) throw new Error(`npm ${res.status}`)
  const body = (await res.json()) as {
    "dist-tags"?: { latest?: string }
  }
  const latest = body["dist-tags"]?.latest
  if (!latest) throw new Error("no latest tag")
  return latest
}

type UpdateResult = {
  ok: boolean
  reason?: string
  error?: string
  hint?: string
  command?: string
  stdout?: string
  stderr?: string
  /** "local-pnpm" when pnpm ran in place, "vercel-auto-redeploy" when we
   *  forwarded to trigger-deploy, "deploy-hook" / "api-redeploy" when
   *  trigger-deploy itself ran. */
  path?: string
  updated?: Array<{ name: string; kind: "dep" | "dev"; from: string; to: string }>
  deployment?: {
    id?: string
    url?: string
    inspectorUrl?: string
    job?: unknown
  }
}

export function StackClient({
  initial,
  adminEmail,
}: {
  initial: DependencySnapshot[]
  adminEmail: string
}) {
  const [rows, setRows] = useState<Row[]>(() =>
    initial.map((d) => ({ ...d, latest: null, status: "loading" as const })),
  )
  const [scanning, setScanning] = useState(false)
  const [scannedAt, setScannedAt] = useState<Date | null>(null)
  const [updating, setUpdating] = useState<null | "local" | "deploy">(null)
  const [updateResult, setUpdateResult] = useState<UpdateResult | null>(null)

  async function scan() {
    setScanning(true)
    const queue = [...rows]
    const updated: Record<string, Row> = {}
    async function worker() {
      while (queue.length > 0) {
        const dep = queue.shift()
        if (!dep) return
        try {
          const latest = await fetchLatest(dep.name)
          updated[dep.name] = {
            ...dep,
            latest,
            status: compareSemver(dep.declared, latest),
          }
        } catch (err) {
          updated[dep.name] = {
            ...dep,
            latest: null,
            status: "unknown",
            error: (err as Error).message,
          }
        }
        setRows((prev) => prev.map((r) => updated[r.name] ?? r))
      }
    }
    await Promise.all(Array.from({ length: 8 }, () => worker()))
    setScannedAt(new Date())
    setScanning(false)
  }

  useEffect(() => {
    void scan()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /**
   * Single entry point for "update now". On the server we detect the
   * runtime environment and pick the right mechanism:
   *   • Vercel production (read-only FS) → forwards to trigger-deploy,
   *     which either POSTs a deploy hook OR calls the Vercel REST API
   *     to redeploy with fresh deps.
   *   • Local dev / self-host → runs pnpm update --latest in-place.
   * The client code stays dumb and just renders whatever comes back.
   */
  async function updateNow() {
    if (
      !window.confirm(
        "Mettre à jour TOUS les paquets ?\n\n• En local : modifie package.json + pnpm-lock.yaml.\n• En production Vercel : déclenche un nouveau build avec « pnpm update --latest ».",
      )
    ) {
      return
    }
    setUpdating("local")
    setUpdateResult(null)
    try {
      const res = await fetch("/api/admin/stack/update", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ scope: "all" }),
      })
      const json = (await res.json()) as UpdateResult
      setUpdateResult(json)
      if (json.ok && (!json.path || json.path === "local-pnpm")) void scan()
    } catch (err) {
      setUpdateResult({
        ok: false,
        error: (err as Error).message,
      })
    } finally {
      setUpdating(null)
    }
  }

  const stats = useMemo(() => {
    const buckets = {
      current: 0,
      patch: 0,
      minor: 0,
      major: 0,
      unknown: 0,
      loading: 0,
    }
    for (const r of rows) buckets[r.status] += 1
    return buckets
  }, [rows])

  return (
    <div style={{ maxWidth: 960 }}>
      <header
        style={{
          marginBottom: "2rem",
          display: "flex",
          flexWrap: "wrap",
          gap: "1rem",
          alignItems: "flex-end",
          justifyContent: "space-between",
        }}
      >
        <div>
          <h1 style={{ marginBottom: "0.25rem" }}>Stack &amp; dépendances</h1>
          <p style={{ color: "var(--theme-elevation-500)", margin: 0 }}>
            {rows.length} paquet{rows.length > 1 ? "s" : ""} déclaré
            {rows.length > 1 ? "s" : ""} dans <code>package.json</code>,
            comparés en temps réel au registre npm.
            {adminEmail ? (
              <>
                {" "}Session admin : <strong>{adminEmail}</strong>.
              </>
            ) : null}
          </p>
        </div>
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "0.5rem",
            justifyContent: "flex-end",
          }}
        >
          <button
            type="button"
            className="btn btn--style-secondary"
            onClick={() => void scan()}
            disabled={scanning || updating !== null}
            title="Interroge registry.npmjs.org pour les dernières versions sans toucher au package.json."
          >
            {scanning ? "Vérification…" : "Vérifier"}
          </button>
          <button
            type="button"
            className="btn btn--style-primary"
            onClick={() => void updateNow()}
            disabled={updating !== null}
            title="Local : pnpm update --latest. Sur Vercel : déclenche un redeploy qui bump les deps dans la build command."
          >
            {updating
              ? "Mise à jour…"
              : "Mettre à jour maintenant"}
          </button>
        </div>
      </header>

      {updateResult ? (
        <section
          style={{
            marginBottom: "2rem",
            padding: "1rem 1.25rem",
            borderRadius: "0.5rem",
            border: `1px solid ${updateResult.ok ? "rgba(16, 185, 129, 0.3)" : "rgba(244, 63, 94, 0.3)"}`,
            background: updateResult.ok
              ? "rgba(16, 185, 129, 0.08)"
              : "rgba(244, 63, 94, 0.08)",
            color: updateResult.ok ? "rgb(4, 120, 87)" : "rgb(190, 18, 60)",
            fontSize: "0.875rem",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-start",
              gap: "1rem",
            }}
          >
            <div style={{ flex: 1, minWidth: 0 }}>
              <strong style={{ display: "block", marginBottom: "0.25rem" }}>
                {updateResult.ok
                  ? updateResult.deployment
                    ? "Redéploiement Vercel déclenché."
                    : `${updateResult.updated?.length ?? 0} paquet(s) mis à jour.`
                  : "Échec de la mise à jour."}
              </strong>
              {updateResult.path ? (
                <p
                  style={{
                    margin: "0 0 0.25rem",
                    fontSize: "0.75rem",
                    opacity: 0.8,
                  }}
                >
                  Méthode :{" "}
                  <code>
                    {updateResult.path === "local-pnpm"
                      ? "pnpm update local"
                      : updateResult.path === "deploy-hook"
                        ? "Vercel Deploy Hook"
                        : updateResult.path === "api-redeploy"
                          ? "Vercel REST API redeploy"
                          : updateResult.path === "vercel-auto-redeploy"
                            ? "Vercel auto-redeploy (lecture seule détectée)"
                            : updateResult.path}
                  </code>
                </p>
              ) : null}
              {updateResult.error ? (
                <p style={{ margin: "0 0 0.25rem" }}>{updateResult.error}</p>
              ) : null}
              {updateResult.hint ? (
                <p
                  style={{
                    margin: "0.5rem 0 0",
                    fontSize: "0.8125rem",
                    color: "var(--theme-elevation-700)",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  {updateResult.hint}
                </p>
              ) : null}
              {updateResult.command ? (
                <p style={{ margin: "0.5rem 0 0", fontSize: "0.75rem" }}>
                  <code>{updateResult.command}</code>
                </p>
              ) : null}
              {updateResult.deployment?.inspectorUrl ? (
                <p style={{ margin: "0.5rem 0 0", fontSize: "0.8125rem" }}>
                  <a
                    href={updateResult.deployment.inspectorUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: "inherit", textDecoration: "underline" }}
                  >
                    Suivre le build sur Vercel ↗
                  </a>
                </p>
              ) : null}
            </div>
            <button
              type="button"
              className="btn btn--style-secondary"
              onClick={() => setUpdateResult(null)}
              style={{ fontSize: "0.75rem", padding: "0.25rem 0.625rem" }}
            >
              Fermer
            </button>
          </div>
          {updateResult.updated && updateResult.updated.length > 0 ? (
            <details style={{ marginTop: "0.75rem" }}>
              <summary style={{ cursor: "pointer", fontSize: "0.8125rem" }}>
                Détail des bumps ({updateResult.updated.length})
              </summary>
              <ul
                style={{
                  margin: "0.5rem 0 0",
                  paddingLeft: "1.25rem",
                  fontSize: "0.8125rem",
                  lineHeight: 1.6,
                  fontFamily: "var(--font-mono, monospace)",
                }}
              >
                {updateResult.updated.map((u) => (
                  <li key={u.name}>
                    <strong>{u.name}</strong> {u.from}{" "}
                    <span style={{ opacity: 0.4 }}>→</span> {u.to}
                  </li>
                ))}
              </ul>
            </details>
          ) : null}
        </section>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
          gap: "0.75rem",
          marginBottom: "2rem",
        }}
      >
        <StatTile label="À jour" value={stats.current} tone="emerald" />
        <StatTile label="Patch disponible" value={stats.patch} tone="slate" />
        <StatTile label="Mineur disponible" value={stats.minor} tone="amber" />
        <StatTile label="Majeur disponible" value={stats.major} tone="rose" />
        {/* Separate tile for "loading" (scan in progress) vs "unknown"
            (registry 404 / parse failed) so the dashboard doesn't
            conflate transient and permanent states. */}
        {stats.loading > 0 ? (
          <StatTile label="Scan en cours…" value={stats.loading} tone="slate" />
        ) : stats.unknown > 0 ? (
          <StatTile label="Non comparables" value={stats.unknown} tone="slate" />
        ) : null}
      </div>

      <section
        style={{
          background: "var(--theme-elevation-50)",
          borderRadius: "0.5rem",
          padding: "1rem 1.25rem",
          marginBottom: "2rem",
          fontSize: "0.875rem",
        }}
      >
        <h3 style={{ marginTop: 0, marginBottom: "0.5rem" }}>
          Automatisation des mises à jour
        </h3>
        <ul
          style={{
            margin: 0,
            paddingLeft: "1.25rem",
            color: "var(--theme-elevation-600)",
            lineHeight: 1.6,
          }}
        >
          <li>
            <strong>Cron Vercel</strong> (<code>vercel.json</code>) —{" "}
            <strong>tous les jours 04:00 UTC</strong> : appelle{" "}
            <code>VERCEL_DEPLOY_HOOK_URL</code>, le build exécute{" "}
            <code>scripts/vercel-install.sh</code> (<code>pnpm install</code> +{" "}
            <code>pnpm update</code>) avant <code>next build</code>. Pas de
            configuration manuelle requise — tout est dans <code>vercel.json</code>.
          </li>
          <li>
            <strong>Renovate</strong> (<code>renovate.json</code>) — lundis 9h
            Europe/Paris : PR groupées par écosystème (Payload, Next.js + React,
            Better-Auth, Tailwind) pour les bumps <em>majeurs</em> que le cron
            quotidien n&apos;applique pas automatiquement.
          </li>
          <li>
            Dev-deps patch + minor : <strong>auto-merge</strong> après tests.
          </li>
          <li>
            Runtime patch : <strong>auto-merge</strong> après tests.
          </li>
          <li>
            Failles CVE / OSV : <strong>auto-merge immédiat</strong>.
          </li>
          <li>Bumps majeurs (Stripe, Next, Payload) : revue manuelle.</li>
        </ul>
        {scannedAt ? (
          <p
            style={{
              marginTop: "0.75rem",
              marginBottom: 0,
              fontSize: "0.75rem",
              color: "var(--theme-elevation-500)",
            }}
          >
            Dernier scan contre registry.npmjs.org :{" "}
            <strong>
              {new Intl.DateTimeFormat("fr-FR", {
                dateStyle: "short",
                timeStyle: "short",
              }).format(scannedAt)}
            </strong>
            {" · "}Prochaine mise à jour automatique :{" "}
            <strong>demain 04:00 UTC</strong>
          </p>
        ) : null}
      </section>

      <section>
        <h3 style={{ marginBottom: "0.5rem" }}>Dépendances déclarées</h3>
        <p style={{ color: "var(--theme-elevation-500)", margin: "0 0 1rem" }}>
          Triées alphabétiquement. Cliquez sur le nom pour ouvrir la fiche npm.
        </p>
        <ul
          style={{
            listStyle: "none",
            padding: 0,
            margin: 0,
            border: "1px solid var(--theme-elevation-150)",
            borderRadius: "0.5rem",
            overflow: "hidden",
          }}
        >
          {rows.map((row, i) => (
            <li
              key={row.name}
              style={{
                display: "flex",
                alignItems: "center",
                gap: "1rem",
                padding: "0.75rem 1rem",
                borderTop:
                  i === 0 ? "none" : "1px solid var(--theme-elevation-100)",
                fontSize: "0.875rem",
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <a
                  href={`https://www.npmjs.com/package/${row.name}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ fontWeight: 600, textDecoration: "none" }}
                >
                  {row.name}
                </a>
                {row.kind === "dev" ? (
                  <span
                    aria-label="Dépendance de développement"
                    style={{
                      marginLeft: "0.5rem",
                      fontSize: "0.625rem",
                      fontWeight: 600,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "var(--theme-elevation-600)",
                      background: "var(--theme-elevation-100)",
                      border: "1px solid var(--theme-elevation-150)",
                      borderRadius: "999px",
                      padding: "0.125rem 0.5rem",
                      verticalAlign: "middle",
                      whiteSpace: "nowrap",
                    }}
                  >
                    dev
                  </span>
                ) : null}
              </div>
              <div
                style={{
                  fontFamily: "var(--font-mono, monospace)",
                  fontSize: "0.75rem",
                  color: "var(--theme-elevation-500)",
                  textAlign: "right",
                  minWidth: 160,
                }}
              >
                {stripRange(row.declared)}{" "}
                <span style={{ opacity: 0.4 }}>→</span>{" "}
                {row.latest ?? "…"}
              </div>
              <StatusPill status={row.status} />
            </li>
          ))}
        </ul>
      </section>
    </div>
  )
}

function StatTile({
  label,
  value,
  tone,
}: {
  label: string
  value: number
  tone: "emerald" | "slate" | "amber" | "rose"
}) {
  const palette: Record<typeof tone, { bg: string; fg: string; border: string }> =
    {
      emerald: {
        bg: "rgba(16, 185, 129, 0.08)",
        fg: "rgb(4, 120, 87)",
        border: "rgba(16, 185, 129, 0.3)",
      },
      slate: {
        bg: "var(--theme-elevation-50)",
        fg: "var(--theme-elevation-800)",
        border: "var(--theme-elevation-150)",
      },
      amber: {
        bg: "rgba(245, 158, 11, 0.08)",
        fg: "rgb(180, 83, 9)",
        border: "rgba(245, 158, 11, 0.3)",
      },
      rose: {
        bg: "rgba(244, 63, 94, 0.08)",
        fg: "rgb(190, 18, 60)",
        border: "rgba(244, 63, 94, 0.3)",
      },
    }
  const c = palette[tone]
  return (
    <div
      style={{
        background: c.bg,
        border: `1px solid ${c.border}`,
        borderRadius: "0.5rem",
        padding: "0.875rem 1rem",
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: "1.5rem",
          fontWeight: 700,
          color: c.fg,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </p>
      <p
        style={{
          margin: "0.125rem 0 0",
          fontSize: "0.75rem",
          fontWeight: 500,
          color: c.fg,
        }}
      >
        {label}
      </p>
    </div>
  )
}

function StatusPill({ status }: { status: Row["status"] }) {
  const label =
    status === "loading"
      ? "Scan…"
      : status === "current"
        ? "À jour"
        : status === "patch"
          ? "Patch"
          : status === "minor"
            ? "Mineur"
            : status === "major"
              ? "Majeur"
              : "N/A"
  const palette: Record<Row["status"], { bg: string; fg: string }> = {
    loading: { bg: "var(--theme-elevation-50)", fg: "var(--theme-elevation-500)" },
    current: { bg: "rgba(16, 185, 129, 0.12)", fg: "rgb(4, 120, 87)" },
    patch: { bg: "var(--theme-elevation-100)", fg: "var(--theme-elevation-700)" },
    minor: { bg: "rgba(245, 158, 11, 0.12)", fg: "rgb(180, 83, 9)" },
    major: { bg: "rgba(244, 63, 94, 0.12)", fg: "rgb(190, 18, 60)" },
    unknown: { bg: "var(--theme-elevation-50)", fg: "var(--theme-elevation-500)" },
  }
  const c = palette[status]
  return (
    <span
      style={{
        background: c.bg,
        color: c.fg,
        fontSize: "0.6875rem",
        fontWeight: 600,
        padding: "0.25rem 0.625rem",
        borderRadius: "999px",
        whiteSpace: "nowrap",
      }}
    >
      {label}
    </span>
  )
}
